import {
  bmiQuizV1,
  bmiQuizV11,
  bmiQuizV12,
  bmiQuizV2,
  type BmiQuizTemplate,
} from "./bmi-quiz";
import type { CoachMemoryCategory } from "./coach-memory";

// TES-1019 (Emelie, point 8) — F-01 fills the patient memory with atomic,
// source-tracked facts. The F-01 summary and the patient memory are two
// different things: the summary gives the quick picture, the memory holds
// one fact per row that can be followed, corrected and traced on its own.
//
// Deterministic and close to verbatim: every fact is the label of an option
// she chose (or the words she typed), behind a short lead-in naming what was
// asked. No interpretation, no diagnosis, nothing she did not answer.
//
// Medical answers stay out of the coach memory: weight, height,
// reproductive status, the prescription-transfer question, weight-loss
// medication and its history, surgery, menstruation and menopause, illness
// or medication as a cause, and the fertility or disease reasons of the
// first quiz generation are the doctor's (the medical note reads them).
// How she found us is not about her at all.

interface FieldRule {
  category: CoachMemoryCategory;
  /** Lead-in before the answer; null writes the answer's label alone. */
  lead: string | null;
  /** Option values that are never written (medical, "other", "don't know"). */
  skip?: readonly string[];
}

// TES-1074 — the lead-ins under which F-01 files her earlier attempts (how
// long she has tried, what she has tried).
//
// TES-1103 (Ö-320) — these facts now go to the "Tidigare försök" area
// (`previous_attempt`). Before it existed they were filed under
// "Återkommande mönster", and rows written then stay there until they are
// refiled (CoachMemoryService.refileF01PreviousAttempts). The leads are what
// recognises such a legacy row: the body is encrypted and no column records
// which F-01 field a row came from.
const LEAD_TRIED = "Har tidigare provat";
const LEAD_HOW_LONG_V2 = "Vikten har varit en utmaning";
const LEAD_HOW_LONG_V1 = "Har försökt gå ner i vikt i";
export const F01_PREVIOUS_ATTEMPT_LEADS: readonly string[] = [
  LEAD_TRIED,
  LEAD_HOW_LONG_V2,
  LEAD_HOW_LONG_V1,
];

/**
 * TES-1103 — an earlier attempt that F-01 filed under "Återkommande mönster"
 * before "Tidigare försök" existed: the old area behind one of the fixed leads.
 */
export function isLegacyF01PreviousAttempt(item: {
  category: string;
  body: string;
}): boolean {
  return (
    item.category === "recurring_pattern" &&
    F01_PREVIOUS_ATTEMPT_LEADS.some((lead) => item.body.startsWith(`${lead}:`))
  );
}

// TES-1101 — the lead-ins under which F-01 files what matters most to her:
// v2's "Vad är viktigast för dig när det gäller din vikt just nu?" and v1's
// reason for wanting to lose weight. The coach's top summary shows these rows
// as what she mainly wants help with (A-01: "vad patienten främst vill ha
// hjälp med").
const LEAD_MOST_IMPORTANT = "Viktigast just nu";
const LEAD_WHY_V1 = "Vill gå ner i vikt för att";
export const F01_MAIN_HELP_LEADS: readonly string[] = [
  LEAD_MOST_IMPORTANT,
  LEAD_WHY_V1,
];

// F-01 from version 2 on (bmiQuizV2's field set).
const RULES_V2: Readonly<Record<string, FieldRule>> = {
  desiredOutcome: {
    category: "goal",
    lead: LEAD_MOST_IMPORTANT,
    // "Annat" is written from her own words (desiredOutcomeOther).
    skip: ["other"],
  },
  desiredOutcomeOther: { category: "goal", lead: LEAD_MOST_IMPORTANT },
  // TES-1321 — SUIT_DESIRED_SUPPORT (F-01 v11). The coach doc: "Endast valda
  // strukturerade svarskoder får användas direkt som AI-kontext.
  // OTHER-fritext kräver beslutad sanering och behörighetskontroll innan den
  // får användas i AI-stöd eller Patientminne." So only the chosen codes are
  // written, as their labels; OTHER is skipped and desiredSupportOther has
  // deliberately NO rule. NOT_SURE_YET is not a kind of support. The
  // MEDICATION_SUPPORT row is a wish, not a medical fact; since TES-1445
  // (Ö-604) the coach draft may read it (F01_AI_CONTEXT_WISHES below).
  desiredSupport: {
    category: "desired_support",
    lead: "Önskat stöd",
    skip: ["OTHER", "NOT_SURE_YET"],
  },
  impactDegree: {
    category: "motivation",
    lead: "Vikten påverkar hälsa eller vardag",
  },
  howLongTried: {
    category: "previous_attempt",
    lead: LEAD_HOW_LONG_V2,
    skip: ["unsure"],
  },
  changeContext: {
    category: "recurring_pattern",
    lead: "Vikten förändrades",
    skip: [
      "after_pregnancy",
      "hormonal_changes",
      "illness_or_medication",
      "dont_know",
      "other",
    ],
  },
  methodsTried: {
    category: "previous_attempt",
    lead: LEAD_TRIED,
    skip: ["medication", "other", "no_structured_attempts"],
  },
  bodySignals: {
    category: "recurring_pattern",
    lead: "Känner igen",
    skip: ["none_of_these"],
  },
  biggestObstacles: {
    category: "challenge",
    lead: "Gör förändring svår",
    skip: ["other"],
  },
  emotionalEating: {
    category: "recurring_pattern",
    lead: "Mat hjälper vid stress, trötthet, känslor eller behov av paus",
    skip: ["dont_know"],
  },
  // TES-1658 — v13 merges bodySignals, biggestObstacles and emotionalEating
  // into this one question, so it carries what they gave the memory. The two
  // life-phase questions get no rule, as lifeStage and changeContext's
  // pregnancy and hormonal answers had none.
  weightDifficulties: {
    category: "challenge",
    lead: "Gör vikten svår att påverka",
    skip: ["dont_know", "other"],
  },
};

// The first quiz generation (bmiQuizV1), still on older submissions.
const RULES_V1: Readonly<Record<string, FieldRule>> = {
  whyLoseWeight: {
    category: "goal",
    lead: LEAD_WHY_V1,
    skip: ["fertility", "medical_condition", "other"],
  },
  impactOnLife: {
    category: "challenge",
    lead: "Övervikten påverkar",
    skip: ["physical_pain", "none"],
  },
  activityLevel: {
    category: "habit_routine",
    lead: "Aktivitetsnivå i vardagen",
  },
  emotionalEating: {
    category: "recurring_pattern",
    lead: "Använder mat för att må bättre (1–5)",
  },
  biggestObstacles: { category: "challenge", lead: "Känner igen sig i" },
  hardestPart: {
    category: "challenge",
    lead: "Svårast i viktresan",
    skip: ["other"],
  },
  howLongTried: {
    category: "previous_attempt",
    lead: LEAD_HOW_LONG_V1,
  },
  methodsTried: {
    category: "previous_attempt",
    lead: LEAD_TRIED,
    skip: ["surgery", "medication", "other"],
  },
  speedPreference: { category: "preference", lead: "Önskat tempo" },
};

// TES-1445 (Ö-604, Emelie 2026-10-19) — the two F-01 answers that read as
// medical but are her wishes: "Båda F-01-svaren ska kunna finnas i
// Patientminnet, vara tillgängliga för coachen och användas som underlag i
// coachens AI-svarsutkast. De återger patientens uttryckta stödbehov och mål.
// De får inte omtolkas till diagnos, medicinsk indikation eller medicinska
// råd." The medical stop catches both texts, so without this list the coach
// draft's memory selection left them out (TES-1321).
//
// Exactly these two option codes, as the row F-01 writes for them. The
// "Annat" text (desiredSupportOther) is not written to the memory at all,
// and her own "Annat" words for desiredOutcome are not on this list: they go
// through the medical stop like every other text (TES-1319, Ö-534).
const F01_AI_CONTEXT_WISH_OPTIONS: readonly {
  fieldKey: string;
  value: string;
}[] = [
  { fieldKey: "desiredSupport", value: "MEDICATION_SUPPORT" },
  { fieldKey: "desiredOutcome", value: "less_pain" },
];

/**
 * TES-1445 — the memory rows (category and body, verbatim as f01MemoryFacts
 * writes them) of the two F-01 wishes a coach draft may read although the
 * medical stop flags their wording.
 */
export const F01_AI_CONTEXT_WISHES: readonly {
  category: CoachMemoryCategory;
  body: string;
}[] = F01_AI_CONTEXT_WISH_OPTIONS.map(({ fieldKey, value }) => {
  const rule = RULES_V2[fieldKey];
  const label = bmiQuizV2.fields
    .find((f) => f.key === fieldKey)
    ?.options?.find((o) => o.value === value)?.label.sv;
  if (!rule?.lead || !label) {
    throw new Error(`F-01 wish ${fieldKey}=${value} is not in the quiz`);
  }
  return { category: rule.category, body: `${rule.lead}: ${label}` };
});

/**
 * TES-1445 — whether a Patientminne row is one of the two F-01 wishes, as
 * F-01 wrote it: her own statement, from her F-01, with no human author, in
 * its area and with its exact text. A row a coach wrote or revised is not.
 */
export function isF01AiContextWish(row: {
  category: string;
  body: string;
  factType: string;
  sourceType: string;
  sourceTemplateKey?: string | null;
  createdByEmployeeId: string | null;
}): boolean {
  return (
    row.factType === "patient_statement" &&
    row.sourceType === "form_submission" &&
    row.sourceTemplateKey === "f01" &&
    row.createdByEmployeeId === null &&
    F01_AI_CONTEXT_WISHES.some(
      (w) => w.category === row.category && w.body === row.body,
    )
  );
}

// Answers that say nothing, whatever the field.
const DECLINED_VALUES: ReadonlySet<string> = new Set(["prefer_not_to_say"]);
const DECLINED_TEXT =
  /^(vill inte (ange|svara)|inget jag vill ange|föredrar att inte svara|avstår)\.?$/i;

export interface F01MemoryFact {
  /** The F-01 field the fact came from. */
  fieldKey: string;
  category: CoachMemoryCategory;
  /** A short Swedish sentence, close to her answer. */
  body: string;
}

/**
 * The quiz a stored F-01 version is read against: v1 its own, v2–v11 the
 * v11 field set, v12 its own, v13 and anything newer the current quiz. TES-1533 — v12
 * reworded the weight question, so an older answer keeps the words it was
 * given to ("Vad väger du idag?"). A row without a version (a lead from
 * before versions were stamped) reads as v11, as it did before v12.
 */
export function f01QuizForVersion(version: number | null): BmiQuizTemplate {
  if (version === bmiQuizV1.version) return bmiQuizV1;
  if (version === null || version <= bmiQuizV11.version) return bmiQuizV11;
  // TES-1658 — v13 replaced six questions; a v12 answer keeps v12's.
  return version === bmiQuizV12.version ? bmiQuizV12 : bmiQuizV2;
}

function textOf(value: unknown): string | null {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

/**
 * The atomic facts one F-01 answer set gives the patient memory, in the
 * quiz's order, one per chosen option. Fields with no rule are not read.
 */
export function f01MemoryFacts(input: {
  templateVersion: number;
  answers: Readonly<Record<string, unknown>>;
}): F01MemoryFact[] {
  const quiz = f01QuizForVersion(input.templateVersion);
  const rules = quiz === bmiQuizV1 ? RULES_V1 : RULES_V2;
  const facts: F01MemoryFact[] = [];
  const seen = new Set<string>();
  const push = (fieldKey: string, rule: FieldRule, answer: string) => {
    if (DECLINED_TEXT.test(answer)) return;
    const body = rule.lead ? `${rule.lead}: ${answer}` : answer;
    const identity = `${rule.category}\u0000${body}`;
    if (seen.has(identity)) return;
    seen.add(identity);
    facts.push({ fieldKey, category: rule.category, body });
  };

  for (const field of quiz.fields) {
    const rule = rules[field.key];
    if (!rule) continue;
    const raw = input.answers[field.key];
    if (raw === undefined || raw === null) continue;
    const options = field.options ?? [];
    if (options.length === 0) {
      const text = textOf(raw);
      if (text) push(field.key, rule, text);
      continue;
    }
    const values = (Array.isArray(raw) ? raw : [raw]).map((v) => String(v));
    for (const value of values) {
      if (DECLINED_VALUES.has(value) || rule.skip?.includes(value)) continue;
      const option = options.find((o) => o.value === value);
      // A value the quiz does not know is not guessed at.
      if (!option) continue;
      push(field.key, rule, option.label.sv);
    }
  }
  return facts;
}
